import { useState } from 'react';
import {
  Box, Typography, Stack, Chip, Switch, Select, MenuItem,
  FormControl, InputLabel, IconButton, Button, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  alpha, useTheme, CircularProgress,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import { useAlertConfigs } from '../../hooks/useAlerts';
import { useUsers } from '../../hooks';
import { useTeams } from '../../hooks';
import { useAuth } from '../../hooks';
import { USER_ROLES } from '../../constants/permissions';

const CATEGORY_LABELS = {
  CONTRACTS: 'Sözleşmeler',
  OFFERS: 'Teklifler',
  JOBS: 'İşler',
  INVOICES: 'Faturalar',
  APPLICATIONS: 'Uygulamalar',
};

const SEVERITY_COLOR = { critical: 'error', warning: 'warning', info: 'info' };

const TARGET_TYPE_LABELS = { role: 'Rol', team: 'Ekip', user: 'Kullanıcı' };

const AddTargetDialog = ({ open, onClose, onAdd, users, teams }) => {
  const [targetType, setTargetType] = useState('role');
  const [targetValue, setTargetValue] = useState('');

  const handleAdd = () => {
    if (!targetValue) return;
    onAdd(targetType, targetValue);
    setTargetValue('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: 15 }}>Hedef Ekle</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <FormControl size="small" fullWidth>
            <InputLabel>Tür</InputLabel>
            <Select value={targetType} label="Tür" onChange={e => { setTargetType(e.target.value); setTargetValue(''); }}>
              <MenuItem value="role">Rol</MenuItem>
              <MenuItem value="team">Ekip</MenuItem>
              <MenuItem value="user">Kullanıcı</MenuItem>
            </Select>
          </FormControl>

          {targetType === 'role' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select value={targetValue} label="Rol" onChange={e => setTargetValue(e.target.value)}>
                {Object.values(USER_ROLES).map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {targetType === 'team' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Ekip</InputLabel>
              <Select value={targetValue} label="Ekip" onChange={e => setTargetValue(e.target.value)}>
                {(teams || []).map(t => (
                  <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {targetType === 'user' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Kullanıcı</InputLabel>
              <Select value={targetValue} label="Kullanıcı" onChange={e => setTargetValue(e.target.value)}>
                {(users || []).map(u => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.firstName} {u.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>İptal</Button>
        <Button size="small" variant="contained" onClick={handleAdd} disabled={!targetValue} disableElevation>
          Ekle
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SendMailDialog = ({ open, config, instances, onClose, onSend, users, teams }) => {
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();

  const handleSend = async () => {
    setSending(true);
    await onSend(config, selected, user?.id);
    setSending(false);
    setSelected([]);
    onClose();
  };

  // Hedefleri alıcı listesine çevir: user → direkt, role/team → o role/takıma ait tüm kullanıcılar
  const recipientOptions = (() => {
    const map = new Map(); // email → recipient (dedup)
    for (const t of (config?.alert_config_targets || [])) {
      let matched = [];
      if (t.target_type === 'user') {
        const u = (users || []).find(u => String(u.id) === t.target_value);
        if (u) matched = [u];
      } else if (t.target_type === 'role') {
        matched = (users || []).filter(u => u.role === t.target_value);
      } else if (t.target_type === 'team') {
        matched = (users || []).filter(u => String(u.teamId) === t.target_value);
      }
      for (const u of matched) {
        if (u.email && !map.has(u.email)) {
          map.set(u.email, {
            email: u.email,
            name: `${u.firstName} ${u.lastName}`,
            user_id: u.id,
            source: t.target_type === 'user' ? null : t.target_type === 'role' ? `Rol: ${t.target_value}` : `Ekip: ${(teams || []).find(tm => String(tm.id) === t.target_value)?.name || t.target_value}`,
          });
        }
      }
    }
    return Array.from(map.values());
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>
        Mail Gönder — {config?.alert_type_config?.label_tr}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Hedef rol/ekip/kullanıcılardan çözümlenen alıcılar aşağıda listeleniyor.
          Göndermek istediklerinizi seçin:
        </Typography>
        <Stack gap={0.75}>
          {recipientOptions.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              Tanımlı hedeflere ait aktif kullanıcı bulunamadı.
            </Typography>
          )}
          {recipientOptions.map(r => (
            <Box
              key={r.email}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid',
                borderColor: selected.some(s => s.email === r.email) ? 'primary.main' : 'divider',
                bgcolor: selected.some(s => s.email === r.email) ? 'primary.50' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onClick={() => setSelected(prev =>
                prev.some(s => s.email === r.email)
                  ? prev.filter(s => s.email !== r.email)
                  : [...prev, r]
              )}
            >
              <Switch
                size="small"
                checked={selected.some(s => s.email === r.email)}
                onChange={() => {}}
                sx={{ pointerEvents: 'none' }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>{r.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{r.email}</Typography>
              </Box>
              {r.source && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                  {r.source}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
        {recipientOptions.length > 1 && (
          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              sx={{ fontSize: 11, color: 'text.secondary' }}
              onClick={() =>
                selected.length === recipientOptions.length
                  ? setSelected([])
                  : setSelected(recipientOptions)
              }
            >
              {selected.length === recipientOptions.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>İptal</Button>
        <Button
          size="small" variant="contained" startIcon={<SendIcon />}
          onClick={handleSend} disabled={selected.length === 0 || sending} disableElevation
        >
          {sending ? <CircularProgress size={13} sx={{ color: 'white' }} /> : `${selected.length} Kişiye Gönder`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfigRow = ({ config, onUpdate, onUpdateSeverity, onAddTarget, onRemoveTarget, users, teams, instances }) => {
  const theme = useTheme();
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const severity = config.alert_type_config?.severity || 'info';
  const instanceCount = instances.filter(i => i.alert_type_code === config.alert_type_code).length;
  const severityColor = theme.palette[SEVERITY_COLOR[severity] || 'info']?.main;

  const handleSend = async (cfg, recipients, triggeredBy) => {
    const apiKey = process.env.REACT_APP_BACKEND_API_KEY || 'AI_THHINKS_API_KEY';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3006';
    const affectedInstances = instances
      .filter(i => i.alert_type_code === cfg.alert_type_code)
      .slice(0, 25)
      .map(i => ({
        label: i.entity_label,
        firm_name: i.extra_data?.firm_name || null,
        extra: i.extra_data,
      }));
    try {
      await fetch(`${backendUrl}/alerts/dispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({
          alert_type_code: cfg.alert_type_code,
          recipients,
          triggered_by_user_id: triggeredBy,
          instance_count: instanceCount,
          affected_items: affectedInstances,
        }),
      });
    } catch {}
  };

  return (
    <Box
      sx={{
        p: 2, borderRadius: 2.5, border: '1px solid',
        borderColor: config.is_enabled ? alpha(severityColor || theme.palette.divider, 0.2) : 'divider',
        bgcolor: config.is_enabled ? alpha(severityColor || theme.palette.grey[500], 0.03) : alpha(theme.palette.grey[100], 0.5),
        opacity: config.is_enabled ? 1 : 0.65,
        transition: 'all 0.2s',
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={0.75} mb={0.5} flexWrap="wrap">
            <Chip
            onClick={() => {}} 
              label={config.alert_type_config?.category}
              size="small"
              sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
            />
            <Chip
            onClick={() => {}} 
              label={severity}
              size="small"
              color={SEVERITY_COLOR[severity] || 'default'}
              sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
            />
            {instanceCount > 0 && (
              <Chip onClick={() => {}}  label={`${instanceCount} aktif`} size="small" color="error"
                sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
            )}
            {!config.is_enabled && (
              <Chip onClick={() => {}}  label="Pasif" size="small" variant="outlined"
                sx={{ height: 18, fontSize: 10, fontWeight: 600, color: 'text.disabled', borderColor: 'divider' }} />
            )}
          </Stack>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, lineHeight: 1.4 }}>
            {config.alert_type_config?.label_tr}
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          {/* Aktif toggle */}
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Switch
              size="small"
              checked={config.is_enabled}
              onChange={e => onUpdate(config.id, { is_enabled: e.target.checked })}
            />
            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 500 }}>Aktif</Typography>
          </Stack>

          {/* Mail toggle */}
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Switch
              size="small"
              checked={config.send_email}
              onChange={e => onUpdate(config.id, { send_email: e.target.checked })}
            />
            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 500 }}>Mail</Typography>
          </Stack>

          {/* Sıklık */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={config.frequency}
              onChange={e => onUpdate(config.id, { frequency: e.target.value })}
              sx={{ fontSize: 12, height: 32, borderRadius: 1.5 }}
            >
              <MenuItem value="daily">Günlük</MenuItem>
              <MenuItem value="weekly">Haftalık</MenuItem>
            </Select>
          </FormControl>

          {/* Önem seviyesi */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={severity}
              onChange={e => onUpdateSeverity(config.alert_type_config?.code, e.target.value)}
              sx={{ fontSize: 12, height: 32, borderRadius: 1.5 }}
            >
              <MenuItem value="info">Bilgi</MenuItem>
              <MenuItem value="warning">Uyarı</MenuItem>
              <MenuItem value="critical">Kritik</MenuItem>
            </Select>
          </FormControl>

          {/* Manuel gönder */}
          {config.send_email && instanceCount > 0 && (
            <Tooltip title="Manuel mail gönder">
              <IconButton size="small" onClick={() => setSendDialogOpen(true)}
                sx={{ border: '1px solid', borderColor: 'primary.main', color: 'primary.main', borderRadius: 1.5 }}>
                <SendIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Hedefler */}
      <Box mt={1.5}>
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>Hedefler:</Typography>
          {(config.alert_config_targets || []).map(t => {
            let label = t.target_value;
            if (t.target_type === 'user') {
              const u = (users || []).find(u => String(u.id) === String(t.target_value));
              label = u ? `${u.firstName} ${u.lastName}`.trim() || u.email : t.target_value;
            } else if (t.target_type === 'team') {
              const tm = (teams || []).find(tm => String(tm.id) === String(t.target_value));
              label = tm ? tm.name : t.target_value;
            }
            return (
            <Chip
             onClick={() => {}} 
              key={t.id}
              label={`${TARGET_TYPE_LABELS[t.target_type]}: ${label}`}
              size="small"
              onDelete={() => onRemoveTarget(config.id, t.id)}
              sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
            />
          );
          })}
          <IconButton size="small" onClick={() => setTargetDialogOpen(true)}
            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      </Box>

      <AddTargetDialog
        open={targetDialogOpen}
        onClose={() => setTargetDialogOpen(false)}
        onAdd={(type, value) => onAddTarget(config.id, type, value)}
        users={users}
        teams={teams}
      />
      <SendMailDialog
        open={sendDialogOpen}
        config={config}
        instances={instances}
        onClose={() => setSendDialogOpen(false)}
        onSend={handleSend}
        users={users}
        teams={teams}
      />
    </Box>
  );
};

const AlertConfigPanel = ({ instances }) => {
  const { configs, loading, updateConfig, updateSeverity, addTarget, removeTarget } = useAlertConfigs();
  const { users } = useUsers();
  const { teams } = useTeams();

  const byCategory = configs.reduce((acc, c) => {
    const cat = c.alert_type_config?.category || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(c);
    return acc;
  }, {});

  if (loading) return (
    <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
  );

  return (
    <Box>
      {Object.entries(byCategory).map(([cat, catConfigs]) => {
        const activeCount = catConfigs.filter(c => c.is_enabled).length;
        const instanceCount = catConfigs.reduce(
          (sum, c) => sum + instances.filter(i => i.alert_type_code === c.alert_type_code).length, 0
        );
        return (
          <Box key={cat} mb={3.5}>
            <Stack direction="row" alignItems="center" gap={1.5} mb={1.5}>
              <Typography
                sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}
              >
                {CATEGORY_LABELS[cat] || cat}
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                {activeCount}/{catConfigs.length} aktif
              </Typography>
              {instanceCount > 0 && (
                <Chip onClick={() => {}}  label={`${instanceCount} kayıt`} size="small" color="error"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
              )}
            </Stack>
            <Stack gap={1}>
              {catConfigs.map(cfg => (
                <ConfigRow
                  key={cfg.id}
                  config={cfg}
                  instances={instances}
                  users={users}
                  teams={teams}
                  onUpdate={updateConfig}
                  onUpdateSeverity={updateSeverity}
                  onAddTarget={addTarget}
                  onRemoveTarget={removeTarget}
                />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default AlertConfigPanel;
